<template>
  <div class="min-h-screen bg-background flex items-center justify-center p-4">
    <div class="max-w-md w-full text-center">
      <!-- Icon: contextual for 404 vs other errors (design tokens) -->
      <div
        class="mx-auto flex items-center justify-center h-20 w-20 rounded-2xl mb-6 transition-colors"
        :class="isNotFound ? 'bg-muted' : 'bg-destructive/10'"
        aria-hidden="true">
        <UIcon
          :name="isNotFound ? 'i-heroicons-magnifying-glass-circle' : 'i-heroicons-exclamation-triangle'"
          class="h-10 w-10"
          :class="isNotFound ? 'text-muted-foreground' : 'text-destructive'"
        />
      </div>

      <!-- Status code for 404 (optional, reinforces clarity) -->
      <p
        v-if="isNotFound"
        class="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
        Error 404
      </p>

      <!-- Title -->
      <h1 class="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-3">
        {{ getErrorTitle() }}
      </h1>

      <!-- Message (body: 16px minimum) -->
      <p class="text-base text-muted-foreground leading-relaxed mb-8">
        {{ getErrorMessage() }}
      </p>

      <!-- Error details: dev only, collapsible to avoid clutter -->
      <div v-if="error && isDev" class="mb-6 text-left">
        <UButton
          v-if="!showDetails"
          variant="ghost"
          color="neutral"
          size="sm"
          class="text-muted-foreground"
          @click="showDetails = true">
          Show technical details
        </UButton>
        <div
          v-show="showDetails"
          class="p-4 bg-card border border-border rounded-xl overflow-auto max-h-48">
          <p class="text-sm font-medium text-foreground mb-2">Error details (dev only)</p>
          <pre class="text-sm text-muted-foreground whitespace-pre-wrap break-words">{{ sanitizedError }}</pre>
        </div>
      </div>

      <!-- Support reference: compact, only status + URL for copy (no stack in UI) -->
      <div
        v-if="error && !isNotFound"
        class="mb-6 p-4 bg-card border border-border rounded-xl text-left">
        <p class="text-sm font-medium text-foreground mb-2">
          <UIcon name="i-heroicons-information-circle" class="h-4 w-4 inline mr-1.5 text-muted-foreground" aria-hidden="true" />
          Reference for support
        </p>
        <div class="text-sm text-muted-foreground space-y-1">
          <p><span class="font-medium text-foreground">Status:</span> {{ error.statusCode ?? '—' }}</p>
          <p><span class="font-medium text-foreground">URL:</span> <span class="break-all">{{ route?.fullPath ?? '—' }}</span></p>
        </div>
      </div>

      <!-- Primary actions: Go Home first (main CTA), then Go Back -->
      <div class="flex flex-col sm:flex-row gap-3 justify-center">
        <UButton
          @click="goHome"
          size="lg"
          class="flex-1 sm:flex-none bg-primary text-primary-foreground hover:bg-primary/90 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded-xl min-h-[44px]"
          aria-label="Go to home page">
          <UIcon name="i-heroicons-home" class="h-5 w-5 mr-2" aria-hidden="true" />
          Go Home
        </UButton>
        <UButton
          @click="handleError"
          size="lg"
          variant="outline"
          class="flex-1 sm:flex-none border-border bg-card text-foreground hover:bg-accent focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded-xl min-h-[44px]"
          aria-label="Go back to previous page">
          <UIcon name="i-heroicons-arrow-left" class="h-5 w-5 mr-2" aria-hidden="true" />
          Go Back
        </UButton>
      </div>

      <!-- Support link (design token: text-secondary) -->
      <p class="mt-8 text-sm text-muted-foreground">
        Need help?
        <NuxtLink
          to="/support"
          class="text-secondary hover:text-secondary/80 font-medium underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background rounded">
          Get Support
        </NuxtLink>
      </p>
    </div>
  </div>
</template>

<script setup>
const error = useError();
const route = useRoute();
const showDetails = ref(false);

const isDev = computed(() => {
  try {
    return import.meta.dev;
  } catch {
    return false;
  }
});

const isNotFound = computed(() => error.value?.statusCode === 404);

// For dev details: show message and status, not full stack in default view
const sanitizedError = computed(() => {
  const e = error.value;
  if (!e) return '';
  return JSON.stringify(
    {
      statusCode: e.statusCode,
      statusMessage: e.statusMessage,
      message: e.message,
      url: e.url ?? route?.fullPath,
    },
    null,
    2
  );
});

onMounted(() => {
  if (error.value && typeof window !== 'undefined' && window.Bugsnag) {
    try {
      window.Bugsnag.notify(error.value, {
        severity: 'error',
        context: 'error-page',
        user: {
          email: useCurrentUser()?.value?.email || 'unknown',
        },
        metaData: {
          error: {
            statusCode: error.value.statusCode,
            message: error.value.message,
            stack: error.value.stack,
          },
          page: {
            url: window.location.href,
            referrer: document.referrer,
          },
        },
      });
    } catch (e) {
      console.warn('Failed to report to Bugsnag:', e);
    }
  }
});

function handleError() {
  clearError();
  useRouter().back();
}

function goHome() {
  clearError();
  navigateTo('/');
}

function getErrorTitle() {
  if (!error.value) return 'Something went wrong';
  switch (error.value.statusCode) {
    case 404:
      return 'Page not found';
    case 403:
      return 'Access denied';
    case 500:
      return 'Server error';
    case 401:
      return 'Sign in required';
    default:
      return 'Something went wrong';
  }
}

function getErrorMessage() {
  if (!error.value) return 'We ran into an unexpected problem. Try again or go home.';
  switch (error.value.statusCode) {
    case 404:
      return 'This page doesn’t exist or was moved. Go home or use the back button to try again.';
    case 403:
      return 'You don’t have permission to view this. Contact support if you think this is a mistake.';
    case 500:
      return 'Our servers hit a snag. Please try again in a moment.';
    case 401:
      return 'Please sign in to view this page.';
    default:
      return 'We ran into an unexpected problem. Try again or go home.';
  }
}
</script>
